import type {
  QuestionnaireVersionOutput,
  QuestionnaireVersionUploadCheckOutput,
  QuestionnaireVersionUploadOutput,
  StandardHierarchyLevelOutput,
} from "@/features/standard";

export const mockStandardHierarchyLevels: StandardHierarchyLevelOutput[] = [
  {
    cod_level: 1,
    name_level_en: "Topic",
    name_level_pt: "Tópico",
    name_level_es: "Tema",
    is_grouping_level: true,
    is_evaluation_level: false,
    is_questioning_level: false,
  },
  {
    cod_level: 2,
    name_level_en: "Principle",
    name_level_pt: "Princípio",
    name_level_es: "Principio",
    is_grouping_level: true,
    is_evaluation_level: false,
    is_questioning_level: false,
  },
  {
    cod_level: 3,
    name_level_en: "Requirement",
    name_level_pt: "Requisito",
    name_level_es: "Requisito",
    is_grouping_level: false,
    is_evaluation_level: true,
    is_questioning_level: false,
  },
  {
    cod_level: 4,
    name_level_en: "Conformance Criteria",
    name_level_pt: "Critérios de conformidade",
    name_level_es: "Criterios de conformidad",
    is_grouping_level: false,
    is_evaluation_level: false,
    is_questioning_level: true,
  },
];

export function buildQuestionnaireVersion(
  overrides: Partial<QuestionnaireVersionOutput> = {}
): QuestionnaireVersionOutput {
  return {
    id: "13",
    code: "GISTM-2020_v2",
    version: "2",
    status: "set",
    is_default: false,
    avi_count: 292,
    cqi_count: 2340,
    upload_id: null,
    file_name: null,
    uploaded_at: "2026-02-10T14:05:00Z",
    created_at: "2026-02-10T14:20:00Z",
    can_delete: true,
    delete_blocked_reason: null,
    associated_projects_count: 0,
    can_set_default: true,
    set_default_blocked_reason: null,
    is_download_available: true,
    ...overrides,
  };
}

export const mockQuestionnaireVersions: QuestionnaireVersionOutput[] = [
  buildQuestionnaireVersion({
    id: null,
    code: null,
    version: null,
    status: "draft",
    avi_count: null,
    cqi_count: null,
    upload_id: "5b1f2c1e-8d3a-4f0e-9a51-2f6d7c9e4b10",
    file_name: "questionnaire_v3.xlsx",
    uploaded_at: "2026-03-19T17:26:00Z",
    created_at: "2026-03-19T17:26:00Z",
    associated_projects_count: null,
    can_set_default: false,
    set_default_blocked_reason: "draft",
    is_download_available: false,
  }),
  buildQuestionnaireVersion(),
  buildQuestionnaireVersion({
    id: "12",
    code: "GISTM-2020_v1",
    version: "1",
    status: "default",
    is_default: true,
    avi_count: 1299,
    cqi_count: 1299,
    uploaded_at: "2026-01-05T09:00:00Z",
    created_at: "2026-01-05T09:30:00Z",
    can_delete: false,
    delete_blocked_reason: "default",
    associated_projects_count: 5,
    can_set_default: false,
    set_default_blocked_reason: "default",
  }),
];

const QUESTIONNAIRE_VERSION_CHECK_CATALOG: [string, string][] = [
  ["required_sheet", 'Required sheets "AVI" and "CQI" are present'],
  ["required_column", "Required header columns are present"],
  ["required_cell", "Required columns are filled in"],
  ["cell_length", "Cell values are within the maximum length"],
  ["id_format", "Row ids are positive whole numbers"],
  ["duplicate_id", "Row ids are unique"],
  ["row_order", "Rows appear in id order"],
  ["sequence_gap", "Row id numbering has no gaps"],
  ["requirement_code_format", '"questioning_code_parent" values follow the code format'],
  ["avi_code_format", '"avi_code" values follow the code format'],
  ["avi_parent_match", 'Each "avi_code" belongs to its "questioning_code_parent"'],
  [
    "avi_parent_in_structure",
    '"questioning_code_parent" values exist in the hierarchical structure',
  ],
  ["duplicate_avi_code", '"avi_code" values are unique in the "AVI" sheet'],
  ["hierarchy_level_count_match", "Codes match the standard's hierarchy levels"],
  ["pillar_format", '"pillar" values are recognized pillar codes'],
  ["cqi_code_format", '"cqi_code" values follow the code format'],
  ["cqi_avi_link", "Each CQI references an existing AVI and each AVI has at least two CQIs"],
  ["cqi_avi_match", 'Each "cqi_code" belongs to its "avi_code_parent"'],
  ["duplicate_cqi_code", '"cqi_code" values are unique'],
  ["cqi_code_sequence_gap", '"cqi_code" numbering has no gaps within each AVI'],
  ["tier_format", '"tier" values are recognized'],
  ["cqi_question_type_format", '"cqi_question_type" values are recognized'],
  ["cqi_tier_question_type_match", '"tier" matches "cqi_question_type" and "is_critical"'],
  ["is_critical_format", '"is_critical" values are true/false (or yes/no)'],
  ["cqi_block_structure", "CQI blocks follow the introductory -> prime -> secondary order"],
];

export function buildQuestionnaireVersionUploadCheck(
  overrides: Partial<QuestionnaireVersionUploadCheckOutput> = {}
): QuestionnaireVersionUploadCheckOutput {
  return {
    check_type: "required_sheet",
    passed: true,
    explanation: 'Required sheets "AVI" and "CQI" are present',
    ...overrides,
  };
}

function buildQuestionnaireVersionUploadChecks(failedCheckTypes: string[] = []) {
  return QUESTIONNAIRE_VERSION_CHECK_CATALOG.map(([checkType, explanation]) =>
    buildQuestionnaireVersionUploadCheck({
      check_type: checkType,
      explanation,
      passed: !failedCheckTypes.includes(checkType),
    })
  );
}

export function buildQuestionnaireVersionUpload(
  overrides: Partial<QuestionnaireVersionUploadOutput> = {}
): QuestionnaireVersionUploadOutput {
  return {
    id: "068c8c51-457c-7b24-8000-41a17b0a37c8",
    file_name: "gistm-questionnaire-v4.xlsx",
    file_size: "312.5 KB",
    uploaded_at: "2026-09-29T14:32:00Z",
    status: "passed",
    checks_passed: 25,
    checks_failed: 0,
    avi_count: 292,
    cqi_count: 2340,
    is_latest: true,
    checks: buildQuestionnaireVersionUploadChecks(),
    ...overrides,
  };
}

export const questionnaireVersionUploadPassedMock = buildQuestionnaireVersionUpload();

export const questionnaireVersionUploadFailedMock = buildQuestionnaireVersionUpload({
  id: "068c8c51-457c-7b24-8000-41a17b0a37c9",
  file_name: "gistm-questionnaire-v4-draft.xlsx",
  file_size: "298.1 KB",
  uploaded_at: "2026-09-29T14:20:00Z",
  status: "failed",
  checks_passed: 24,
  checks_failed: 1,
  avi_count: 290,
  cqi_count: 2331,
  checks: buildQuestionnaireVersionUploadChecks(["cqi_tier_question_type_match"]),
});

export const questionnaireVersionUploadHistoryMock: QuestionnaireVersionUploadOutput[] = [
  { ...questionnaireVersionUploadPassedMock, is_latest: true, checks: [] },
  { ...questionnaireVersionUploadFailedMock, is_latest: false, checks: [] },
  buildQuestionnaireVersionUpload({
    id: "068c8c51-457c-7b24-8000-41a17b0a37ca",
    file_name: "gistm-questionnaire-v3.xlsx",
    file_size: "301.4 KB",
    uploaded_at: "2026-09-28T10:05:00Z",
    status: "failed",
    checks_passed: 22,
    checks_failed: 3,
    avi_count: 287,
    cqi_count: 2298,
    is_latest: false,
    checks: [],
  }),
];

export const questionnaireVersionUploadDetailMock = questionnaireVersionUploadFailedMock;

export const createdQuestionnaireVersionMock = buildQuestionnaireVersion({
  id: "14",
  code: "GISTM-2020_v4",
  version: "4",
  uploaded_at: "2026-09-29T14:32:00Z",
  created_at: "2026-09-29T14:35:00Z",
});
